"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import MobileMenu from "./MobileMenu";
import SearchModal from "@/components/common/SearchModal";

interface HeaderProps {
  title?: string;
  description?: string;
  showNavigation?: boolean;
  transparent?: boolean;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({
  title: _title = "TK",
  description: _description,
  showNavigation = true,
  transparent: _transparent = false,
  className = ""
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { pathname } = useLocation();

  // スクロール検知
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // メニューの開閉
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // 検索モーダルの開閉
  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  // キーボードショートカット
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openSearch]);

  // メニューが開いているときのbodyスクロール制御
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // MobileMenu.tsx と必ず同じ項目にする（デスクトップとモバイルで見えるページが違う状態を作らない）
  const navigationItems = [
    { href: "/",            label: "Home" },
    { href: "/observatory", label: "Observatory" },
    { href: "/blog",        label: "Blog" },
    { href: "/about",       label: "About" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 pointer-events-none ${
          isScrolled ? 'bg-black/80 backdrop-blur-md' : 'bg-transparent'
        } ${className}`}
      >
        <div className="px-6 md:px-12">
          <div className="flex items-center justify-between py-4 md:py-6">
            {/* ロゴ */}
            <Link
              to="/"
              className="pointer-events-auto"
              aria-label="ホームへ戻る"
            >
              <span className="text-[10px] tracking-[0.4em] uppercase text-white/60 hover:text-white transition-colors duration-300 font-mono">
                TK
              </span>
            </Link>

            {/* デスクトップナビゲーション */}
            {showNavigation && (
              <nav className="hidden md:flex items-center gap-10 pointer-events-auto" aria-label="メインナビゲーション">
                {navigationItems.map((item) => {
                  const isActive = pathname === item.href || (item.href !== "/" && pathname.startsWith(item.href));
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      aria-label={`${item.label} ページへ移動`}
                      className={`text-[11px] tracking-[0.15em] transition-colors duration-300 focus:outline-none ${
                        isActive ? 'text-white' : 'text-white/50 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}

                {/* 検索ボタン */}
                <button
                  onClick={openSearch}
                  className="text-white/50 hover:text-white transition-colors duration-300 focus:outline-none"
                  aria-label="サイト内検索 (⌘K)"
                >
                  <Search className="w-3.5 h-3.5" />
                </button>
              </nav>
            )}

            {/* モバイルコントロール */}
            {showNavigation && (
              <div className="flex items-center gap-4 pointer-events-auto md:hidden">
                <button
                  onClick={openSearch}
                  className="text-white/50 hover:text-white transition-colors focus:outline-none"
                  aria-label="サイト内検索"
                >
                  <Search className="w-4 h-4" />
                </button>
                <button
                  onClick={toggleMenu}
                  className="text-white/50 hover:text-white transition-colors focus:outline-none"
                  aria-label="メニューを開く"
                  aria-expanded={isMenuOpen}
                >
                  <div className="flex flex-col gap-1.5 w-5">
                    <span className={`block h-px bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-[5px]' : ''}`} />
                    <span className={`block h-px bg-current transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`} />
                    <span className={`block h-px bg-current transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-[5px]' : ''}`} />
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* モバイルメニュー */}
      <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

      {/* 検索モーダル */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={closeSearch}
      />
    </>
  );
};

export default Header;
